/**
 * "With this support and nothing else changed" — the four numbers steps 1 and 2 show (rebuild M3,
 * 19 Oct 2026), priced by THE ENGINE'S OWN baseline (computeSupportAtHomeBaselineData) on the same
 * household and options the plans use (sahEngineContext.ts). So step 2's numbers are the strip on
 * step 3 by construction, not by coincidence.
 *
 *  - their contribution: the service plan at the assessed rates, within each person's budget, with
 *    anything above it at full price (ServicePlanUtils calculateServicePlanCosts — the engine's split);
 *  - the Age Pension: the yearly assessment;
 *  - each year: year one of the projection (the engine's getProjectionCashFlow);
 *  - left to the family: the projection's row at the horizon.
 */
import { estateOf } from '../../../../shared/src/calculation/support/estateCgt.ts';
import { useEffect, useMemo, useState } from 'react';
import * as calculationApi from '../../services/calculationApi.ts';
import { calculateServicePlanCosts, servicePlanBudgetsOf, type ServicePlanCosts } from '../../../ServicePlanUtils.ts';
import { chosenAgedCarePlanOf, sahEngineContext, sahMoveAnswer, sahPricedServicePlan } from './sahEngineContext.ts';
import { useSahLinkedPosition } from './useSahLinkedPosition.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import type { AgedCareIntegrationDetails, SavedStrategy } from '../../strategy/StrategyIntegrationUtils.ts';

export interface SahBaselineFigures {
    contribution: number;
    pension: number;
    cashflow: number;
    legacy: number;
    rates: { independence: number; everydayLiving: number } | null;
    costs: ServicePlanCosts;
    /** Each person's own contribution on their services (for the lifetime limit). */
    perPerson: { client: number; partner: number };
    projections: Array<Record<string, unknown>>;
    financials: Record<string, unknown>;
    assessableAssets: number;
    assessableIncome: number;
}

/**
 * The aged care plan the figures include, when a partner is moving into care (owner, 24 Oct 2026: "it
 * should clearly call this out ... a client shouldn't need to open up the financial modelling"): the
 * plan, the household before and after it, and anything about it that could not be carried.
 */
export interface SahLinkedCare {
    record: SavedStrategy;
    details: AgedCareIntegrationDetails | null;
    /** The profile as entered. */
    before: DraftFactFind;
    /** The household the figures are priced on: the aged care plan carried out, its living costs. */
    after: DraftFactFind;
    /** True when the whole plan was carried; false when only its deposit could be. */
    whole: boolean;
    caveats: string[];
    reason?: string;
}

type Plan = { client?: Record<string, unknown>; partner?: Record<string, unknown> };

export function useSahBaseline(args: {
    factFind: DraftFactFind;
    servicePlan: Plan;
    years: number;
    supportStartDate?: string;
    savedStrategies: ReadonlyArray<SavedStrategy>;
    /** Extra options, e.g. the illness-separated residency for "what if" rates. */
    extraStrategyOptions?: Record<string, unknown>;
    debounceMs?: number;
    /** False: no call is made (a figure the screen is not showing). */
    enabled?: boolean;
}): { figures: SahBaselineFigures | null; loading: boolean; error: string | null; linkedCare: SahLinkedCare | null } {
    const { factFind, servicePlan, years, supportStartDate, savedStrategies, extraStrategyOptions, debounceMs = 350 } = args;
    const priced = useMemo(() => sahPricedServicePlan(servicePlan, factFind), [servicePlan, factFind]);
    const linkedRecord = useMemo(() => (sahMoveAnswer(factFind) === 'yes' ? chosenAgedCarePlanOf(savedStrategies) : null), [factFind, savedStrategies]);
    const position = useSahLinkedPosition(factFind, linkedRecord);
    // Nothing is priced until the aged care plan has been carried out, so no figure is shown on the wrong household.
    const enabled = (args.enabled ?? true) && position.ready;
    const ctx = useMemo(() => sahEngineContext({ factFind, savedStrategies, supportStartDate, positioned: position.positioned }), [factFind, savedStrategies, supportStartDate, position.positioned]);
    const linkedCare = useMemo<SahLinkedCare | null>(() => (ctx.linkedRecord && position.ready ? {
        record: ctx.linkedRecord,
        details: ctx.linked,
        before: factFind,
        after: ctx.baselineFactFind,
        whole: !!position.positioned,
        caveats: position.caveats,
        reason: position.reason,
    } : null), [ctx, factFind, position]);
    // The household itself is part of the key: a change to the aged care plan or the profile re-prices.
    const key = JSON.stringify([enabled, priced, years, ctx.evaluationOptions, extraStrategyOptions ?? null, ctx.baselineFactFind]);
    const [state, setState] = useState<{ figures: SahBaselineFigures | null; loading: boolean; error: string | null }>({ figures: null, loading: true, error: null });

    useEffect(() => {
        let cancelled = false;
        if (!enabled) { setState({ figures: null, loading: false, error: null }); return undefined; }
        setState((s) => ({ ...s, loading: true }));
        const timer = setTimeout(() => {
            const strategyOptions = { ...(ctx.evaluationOptions.strategyOptions || {}), ...(extraStrategyOptions || {}) };
            const options = { strategyOptions, contributionOptions: { ...ctx.evaluationOptions.contributionOptions, strategyOptions } };
            calculationApi.computeSupportAtHomeBaselineData(ctx.baselineFactFind, priced as Record<string, unknown>, years, options)
                .then((r: { baselineFinancials?: Record<string, unknown>; baselineContributions?: Record<string, { contributionRates?: Record<string, unknown> }>; baselineProjections?: Array<Record<string, unknown>> }) => {
                    if (cancelled) return;
                    const isCouple = factFind?.adviceFor === 'couple';
                    const branch = isCouple ? (r.baselineContributions?.combined || r.baselineContributions?.client) : r.baselineContributions?.client;
                    const rates = (branch?.contributionRates || {}) as Record<string, unknown>;
                    const budgets = servicePlanBudgetsOf(ctx.baselineFactFind as never);
                    const costs = calculateServicePlanCosts(priced as never, rates as never, isCouple, budgets);
                    const one = (who: 'client' | 'partner') => calculateServicePlanCosts({ client: who === 'client' ? priced.client || {} : {}, partner: who === 'partner' ? priced.partner || {} : {} } as never, rates as never, isCouple, budgets).contribution.total;
                    const rows = r.baselineProjections || [];
                    const atHorizon = rows[years - 1] ?? rows[rows.length - 1];
                    const fin = r.baselineFinancials || {};
                    setState({
                        loading: false,
                        error: null,
                        figures: {
                            contribution: costs.contribution.total,
                            pension: Number(fin.socialSecurity) || 0,
                            cashflow: Number(rows[0]?.cashFlow) || 0,
                            legacy: estateOf(atHorizon) ?? 0,
                            rates: { independence: Number(rates.independence) || 0, everydayLiving: Number(rates.everydayLiving) || 0 },
                            costs,
                            perPerson: { client: one('client'), partner: isCouple ? one('partner') : 0 },
                            projections: rows,
                            financials: fin,
                            assessableAssets: Number(fin.assessableAssets) || 0,
                            assessableIncome: Number(fin.totalAssessableIncome) || 0,
                        },
                    });
                })
                .catch(() => { if (!cancelled) setState({ figures: null, loading: false, error: 'The figures could not be worked out just now.' }); });
        }, debounceMs);
        return () => { cancelled = true; clearTimeout(timer); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key]);

    return { ...state, loading: state.loading || !position.ready, linkedCare };
}
